import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { customerAPI, projectAPI, userAPI } from '../../services/api';
import CustomerModal from '../CustomerModal/CustomerModal';
import ProjectModal from '../ProjectModal/ProjectModal';
import { contactDisplayName } from '../../utils/contactName';
import CustomFieldValues from '../CustomFields/CustomFieldValues';
import './customerDetail.css';

const statusLabels = [
    'Lead', 'Write Estimate', 'Pending Signature', 'Signed Contract',
    'Pending Start Date', 'Jobs In Progress', 'Job Completed',
    'Pending Payments', 'Cancelled', 'Lost Follow Up', 'Paid / Closed'
];

const CONTACT_TYPE_LABELS = [
    'Homeowner',
    'Property Manager',
    'Subcontractor',
    'Supplier',
    'Inspector',
    'Adjuster',
    'Other',
    'Tenant',
];

const getStatusBadgeClass = (status) => {
    const statusMap = {
        0: 'status-pending', 1: 'status-pending', 2: 'status-pending',
        3: 'status-progress', 4: 'status-progress', 5: 'status-progress',
        6: 'status-completed', 7: 'status-pending', 8: 'status-cancelled',
        9: 'status-cancelled', 10: 'status-completed'
    };
    return statusMap[status] || 'status-pending';
};

const CustomerDetail = ({ customerId, onBack, onViewProject }) => {
    const [customer, setCustomer] = useState(null);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [showProjectModal, setShowProjectModal] = useState(false);

    const fetchCustomer = useCallback(async () => {
        const response = await customerAPI.getById(customerId);
        setCustomer(response.data);
    }, [customerId]);

    const fetchProjects = useCallback(async () => {
        try {
            const response = await projectAPI.getByCustomer(customerId);
            setProjects(response.data);
        } catch {
            setProjects([]);
        }
    }, [customerId]);

    useEffect(() => {
        const loadAll = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchCustomer(), fetchProjects()]);
            } catch (err) {
                setError('Failed to load customer');
                console.error('Error loading customer:', err);
            } finally {
                setLoading(false);
            }
        };
        if (customerId) loadAll();
    }, [customerId, fetchCustomer, fetchProjects]);

    // Only needed to populate the sales-rep picker when adding a project from here.
    // A failure isn't fatal — the picker is optional — so it doesn't block the page.
    useEffect(() => {
        userAPI.getAll()
            .then(res => setUsers(res.data || []))
            .catch(err => console.error('Error loading users:', err));
    }, []);

    // The project modal only ever creates for this contact, so its picker is
    // seeded and its options list is just this one contact. Memoised because the
    // modal re-seeds its form whenever the seed's identity changes.
    const projectSeed = useMemo(() => ({ customerId }), [customerId]);
    const customerOptions = useMemo(() => (customer ? [customer] : []), [customer]);

    if (loading) return <div className="loading">Loading contact...</div>;
    if (error) return (
        <div className="cd-page">
            <div className="error-message">{error}</div>
            <button className="btn-back" onClick={onBack}>Back to Contacts</button>
        </div>
    );
    if (!customer) return null;

    const displayName = contactDisplayName(customer);
    const typeLabel = CONTACT_TYPE_LABELS[customer.type] || 'Unknown';
    // List every phone field that's actually populated, with its label.
    const phoneEntries = [
        ['Primary', customer.phone],
        ['Main', customer.mainPhone],
        ['Mobile', customer.mobilePhone],
        ['Work', customer.workPhone],
    ].filter(([, v]) => v);
    // Prefer property address; fall back to billing for legacy homeowner rows.
    const street = customer.propertyStreet || customer.billingStreet;
    const city = customer.propertyCity || customer.billingCity;
    const state = customer.propertyState || customer.billingState;
    const zip = customer.propertyZipCode || customer.billingZipCode;

    return (
        <div className="cd-page">
            <button className="btn-back" onClick={onBack}>
                <i className="fas fa-arrow-left"></i> Back to Contacts
            </button>

            <div className="cd-header">
                <div className="cd-header-info">
                    <h1 className="cd-name">{displayName}</h1>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                        <span className={`contact-type-badge type-${customer.type}`}>{typeLabel}</span>
                        {customer.companyName && customer.companyName !== displayName && (
                            <p className="cd-company" style={{ margin: 0 }}>{customer.companyName}</p>
                        )}
                    </div>
                </div>
                <div className="cd-header-actions">
                    <button
                        className="cd-add-project-btn"
                        onClick={() => setShowProjectModal(true)}
                    >
                        <i className="fas fa-plus"></i> Add Project
                    </button>
                    <button
                        className="cd-edit-btn"
                        onClick={() => setShowEditModal(true)}
                    >
                        <i className="fas fa-edit"></i> Edit Contact
                    </button>
                </div>
            </div>

            <div className="cd-layout">
                {/* Contact & Address Info */}
                <div className="cd-info-cards">
                    <div className="cd-card">
                        <h3 className="cd-card-title">
                            <i className="fas fa-address-book"></i> Contact Information
                        </h3>
                        {customer.email ? (
                            <div className="cd-field">
                                <span className="cd-label">Email</span>
                                <span className="cd-value">
                                    <a href={`mailto:${customer.email}`}>{customer.email}</a>
                                </span>
                            </div>
                        ) : (
                            <div className="cd-field">
                                <span className="cd-label">Email</span>
                                <span className="cd-value" style={{ color: 'var(--text-muted)' }}>—</span>
                            </div>
                        )}
                        {phoneEntries.length === 0 ? (
                            <div className="cd-field">
                                <span className="cd-label">Phone</span>
                                <span className="cd-value" style={{ color: 'var(--text-muted)' }}>—</span>
                            </div>
                        ) : (
                            phoneEntries.map(([label, num]) => (
                                <div className="cd-field" key={label}>
                                    <span className="cd-label">{label}</span>
                                    <span className="cd-value">
                                        <a href={`tel:${num}`}>{num}</a>
                                    </span>
                                </div>
                            ))
                        )}
                        {customer.gateCode && (
                            <div className="cd-field">
                                <span className="cd-label">Gate Code</span>
                                <span className="cd-value">{customer.gateCode}</span>
                            </div>
                        )}
                        <div className="cd-field">
                            <span className="cd-label">Added</span>
                            <span className="cd-value">
                                {new Date(customer.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric', month: 'long', day: 'numeric'
                                })}
                            </span>
                        </div>
                    </div>

                    <div className="cd-card">
                        <h3 className="cd-card-title">
                            <i className="fas fa-map-marker-alt"></i> Address
                        </h3>
                        <div className="cd-field">
                            <span className="cd-label">Street</span>
                            <span className="cd-value">{street || 'N/A'}</span>
                        </div>
                        {customer.addressLine2 && (
                            <div className="cd-field">
                                <span className="cd-label">Line 2</span>
                                <span className="cd-value">{customer.addressLine2}</span>
                            </div>
                        )}
                        <div className="cd-field">
                            <span className="cd-label">City</span>
                            <span className="cd-value">{city || 'N/A'}</span>
                        </div>
                        <div className="cd-field">
                            <span className="cd-label">State</span>
                            <span className="cd-value">{state || 'N/A'}</span>
                        </div>
                        <div className="cd-field">
                            <span className="cd-label">Zip</span>
                            <span className="cd-value">{zip || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Admin-defined fields. Renders nothing until one is defined. */}
                <div className="cd-custom-fields">
                    <CustomFieldValues entity="Contact" entityId={customer.id} />
                </div>

                {/* Projects */}
                <div className="cd-projects-section">
                    <h3 className="cd-section-title">
                        <i className="fas fa-project-diagram"></i> Projects ({projects.length})
                    </h3>
                    {projects.length === 0 ? (
                        <div className="cd-empty">
                            <i className="fas fa-folder-open"></i>
                            <p>No projects yet for this customer.</p>
                            <button
                                className="cd-add-project-btn"
                                style={{ margin: '0 auto' }}
                                onClick={() => setShowProjectModal(true)}
                            >
                                <i className="fas fa-plus"></i> Add Project
                            </button>
                        </div>
                    ) : (
                        <div className="cd-projects-list">
                            {projects.map(project => (
                                <div
                                    key={project.id}
                                    className="cd-project-row"
                                    onClick={() => onViewProject && onViewProject(project.id)}
                                >
                                    <div className="cd-project-info">
                                        <span className="cd-project-name">
                                            {project.projectName}
                                            {project.poNumber && (
                                                <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-muted, #57606a)', fontWeight: 'normal' }}>
                                                    {project.poNumber}
                                                </span>
                                            )}
                                        </span>
                                        {project.description && (
                                            <span className="cd-project-desc">{project.description}</span>
                                        )}
                                    </div>
                                    <div className="cd-project-meta">
                                        <span className={`status-badge ${getStatusBadgeClass(project.status)}`}>
                                            {statusLabels[project.status] || 'Unknown'}
                                        </span>
                                        {project.estimatedCost != null && (
                                            <span className="cd-project-cost">
                                                ${project.estimatedCost.toLocaleString()}
                                            </span>
                                        )}
                                    </div>
                                    <i className="fas fa-chevron-right cd-project-arrow"></i>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {showEditModal && (
                <CustomerModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onCustomerCreated={fetchCustomer}
                    editingCustomer={customer}
                />
            )}

            {showProjectModal && (
                <ProjectModal
                    isOpen={showProjectModal}
                    onClose={() => setShowProjectModal(false)}
                    onProjectCreated={fetchProjects}
                    seedProject={projectSeed}
                    customers={customerOptions}
                    users={users}
                />
            )}
        </div>
    );
};

export default CustomerDetail;
